import { useEffect, useMemo, useState } from "react";
import { Alert, Card, Col, Container, Form, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import type { ISiteOption } from "../../customTypings/customer/Customer";
import type { IWasteReturnsSummary } from "../../customTypings/wasteMovement/WasteMovement";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { sitesService } from "../../services/sites/sites.service";
import { wasteReturnsService } from "../../services/wasteReturns/wasteReturns.service";
import { getUserFacingErrorMessage, ListPageTitles, logErrorForDev, SectionLabels } from "../../utils";

function currentQuarterRange(): { from: string; to: string } {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const from = new Date(now.getFullYear(), quarter * 3, 1);
    const to = new Date(now.getFullYear(), quarter * 3 + 3, 0);
    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

const WasteReturnsPage = () => {
    const [sites, setSites] = useState<ISiteOption[]>([]);
    const [siteId, setSiteId] = useState<string>("");
    const quarter = useMemo(() => currentQuarterRange(), []);
    const [dateFrom, setDateFrom] = useState(quarter.from);
    const [dateTo, setDateTo] = useState(quarter.to);
    const [summary, setSummary] = useState<IWasteReturnsSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const formAutoFocusRef = useFormAutoFocus(sites.length > 0, [sites.length]);

    useEffect(() => {
        sitesService
            .listForOrganisation()
            .then((list) => {
                setSites(list);
                if (list.length === 1) setSiteId(list[0].id);
            })
            .catch((e: unknown) => logErrorForDev("WasteReturnsPage.sites", e));
    }, []);

    const loadSummary = () => {
        setLoading(true);
        setError(null);
        wasteReturnsService
            .summary({
                siteId: siteId || null,
                movementDateFrom: dateFrom,
                movementDateTo: dateTo,
            })
            .then(setSummary)
            .catch((e: unknown) => setError(getUserFacingErrorMessage(e, "Could not load summary.")))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (sites.length === 0) return;
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sites.length, siteId, dateFrom, dateTo]);

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.wasteReturns}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                items={[
                    { text: SectionLabels.operations, to: "/operations" },
                    { text: ListPageTitles.wasteReturns },
                ]}
            />
            <h1>{ListPageTitles.wasteReturns}</h1>
            <p className="text-muted">Review accumulated waste received and removed data for a site and reporting period.</p>

            <form ref={formAutoFocusRef}>
            <Row className="mb-4 g-3">
                {sites.length > 1 && (
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Site</Form.Label>
                            <Form.Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                                <option value="">All accessible sites</option>
                                {sites.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.siteName}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                )}
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>From</Form.Label>
                        <Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>To</Form.Label>
                        <Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </Form.Group>
                </Col>
            </Row>
            </form>

            {error && <Alert variant="danger">{error}</Alert>}
            {loading && <Alert variant="info">Loading summary…</Alert>}

            {summary && !loading && (
                <>
                    <Alert variant="info">{summary.placeholderMessage}</Alert>
                    <Row className="g-3 mb-4">
                        <Col md={3}>
                            <Card body>
                                <div className="text-muted small">Waste Received records</div>
                                <div className="fs-4 fw-semibold">{summary.receivedCount}</div>
                                <div className="text-muted small">{summary.receivedTonnesTotal.toFixed(3)} t total</div>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card body>
                                <div className="text-muted small">Waste Removed records</div>
                                <div className="fs-4 fw-semibold">{summary.removedCount}</div>
                                <div className="text-muted small">{summary.removedTonnesTotal.toFixed(3)} t total</div>
                            </Card>
                        </Col>
                    </Row>
                    <h2 className="h5">Reporting status</h2>
                    <Row className="g-3">
                        {(
                            [
                                ["Draft", summary.reportingStatusCounts.draft],
                                ["Ready", summary.reportingStatusCounts.ready],
                                ["Included", summary.reportingStatusCounts.included],
                                ["Excluded", summary.reportingStatusCounts.excluded],
                                ["Corrected", summary.reportingStatusCounts.corrected],
                            ] as const
                        ).map(([label, count]) => (
                            <Col md={2} key={label}>
                                <Card body className="text-center">
                                    <div className="text-muted small">{label}</div>
                                    <div className="fs-5 fw-semibold">{count}</div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </>
            )}
        </Container>
    );
};

export { WasteReturnsPage };
